/**
 * Custom Bass Voice storage — user-tunable bass presets that clone a
 * built-in synth bass and override macro knobs, persisted in localStorage.
 *
 * Schema (one entry):
 *   { id:        "custom-bass-<random>",
 *     label:     "Greasy Acid Lead",
 *     basedOn:   "bass-acid",                  // built-in id the user cloned
 *     macros:    { decay, drive, subMix, pitch },
 *     createdAt: ISO date string }
 *
 * Storage key: `elpm-custom-bass-voices`.
 *
 * The macro schema is defined in sampleBank.js (BASS_MACRO_SCHEMA) and used
 * here only for clamping. No backend round-trip.
 */

import { clampBassMacros } from "./bassMacros.js";

const STORAGE_KEY = "elpm-custom-bass-voices";

const BUILTIN_BASS_IDS = new Set([
  "bass-synth", "bass-sub", "bass-slap", "bass-reese",
  "bass-acid", "bass-moog", "bass-fm", "bass-pluck", "bass-wobble",
]);

export function loadCustomBasses() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveCustomBasses(list) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
}

export function addCustomBass({ label, basedOn, macros }) {
  const safeLabel = (label || "").trim().slice(0, 40) || "Untitled bass";
  const safeBase = BUILTIN_BASS_IDS.has(basedOn) ? basedOn : "bass-synth";
  const entry = {
    id: `custom-bass-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    label: safeLabel,
    basedOn: safeBase,
    macros: clampBassMacros(macros),
    createdAt: new Date().toISOString(),
  };
  const all = loadCustomBasses();
  all.unshift(entry);
  saveCustomBasses(all);
  return entry;
}

export function updateCustomBass(id, patch) {
  const all = loadCustomBasses();
  const idx = all.findIndex((b) => b.id === id);
  if (idx < 0) return null;
  const next = { ...all[idx], ...patch };
  if (patch.macros) next.macros = clampBassMacros({ ...all[idx].macros, ...patch.macros });
  all[idx] = next;
  saveCustomBasses(all);
  return next;
}

export function removeCustomBass(id) {
  const all = loadCustomBasses();
  const next = all.filter((b) => b.id !== id);
  if (next.length === all.length) return false;
  saveCustomBasses(next);
  return true;
}

export function findCustomBass(id) {
  return loadCustomBasses().find((b) => b.id === id) || null;
}

export function isCustomBassId(id) {
  return typeof id === "string" && id.startsWith("custom-bass-");
}
